/**
 * Execution Safety Simulator Page (F035)
 *
 * Route: /execution-simulator | Nav: Model > Execution Simulator
 *
 * Runs REGE's own read-only decision path (F033 blast + F040 policy + the pure decision ladder)
 * against a HYPOTHETICAL execution request — no F037 ledger write, no real execution decision.
 * The simulate form is the page's primary purpose (rendered inline, not behind a Dialog); a run
 * history list of prior simulations sits below it, built on the List & Grid Standard — see
 * `.github/context/list-grid-standard.context.md`, mirroring `src/pages/McpGovernancePage.tsx`
 * (F032) for list/detail conventions.
 *
 * Binds to `ExecutionSimulatorController` (`api/portal/execution-simulator`).
 */
import { useEffect, useState } from 'react';
import type { FC, FormEvent, ReactNode } from 'react';
import {
  Body1,
  Card,
  CardHeader,
  Badge,
  Button,
  Input,
  Dropdown,
  Option,
  Spinner,
  Toaster,
  Toast,
  ToastTitle,
  ToastBody,
  useId,
  useToastController,
  makeStyles,
  tokens,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Field,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Text,
  Caption1,
} from '@fluentui/react-components';
import type { BadgeProps } from '@fluentui/react-components';
import { BeakerRegular, PlayRegular, ChevronRightRegular, HistoryRegular } from '@fluentui/react-icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { executionSimulatorApi } from '../services/api';
import type { SimulateExecutionInput, SimulationResult, SimulationRunPagedResult } from '../types';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import {
  ListPageLayout,
  MasterDetailHeader,
  Pager,
  actionsColumnWidth,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { ListQueryParams, PagedResult } from '../components/list';
import {
  DetailDrawer,
  DetailField,
  DetailMetadataGrid,
  DetailBadges,
} from '../components/common/DetailDrawer';

// ---------------------------------------------------------------------------
// Enum option lists — `executionMode` mirrors `ExecutionMode` (Interactive/Automated/Autonomous);
// `reversibility` is a free-choice, non-enum field on the API contract — offered as a Dropdown
// with the three conventional values plus "Not Specified", not enforced server-side.
// ---------------------------------------------------------------------------

const EXECUTION_MODE_OPTIONS = ['Interactive', 'Automated', 'Autonomous'] as const;
const REVERSIBILITY_OPTIONS = ['Reversible', 'Irreversible', 'Unknown'] as const;

// ---------------------------------------------------------------------------
// Badge color mapping — semantic map, not raw hex (per List & Grid Standard §15).
// `predictedVerdict` is a plain string (no shared TS enum), so the color is inferred from
// well-known verdict vocabulary substrings rather than an exhaustive switch.
// ---------------------------------------------------------------------------

const verdictColor = (verdict: string): BadgeProps['color'] => {
  const v = verdict.toLowerCase();
  if (v.includes('deny') || v.includes('block') || v.includes('reject')) return 'danger';
  if (v.includes('approval') || v.includes('conditional') || v.includes('warn') || v.includes('restrict')) {
    return 'warning';
  }
  if (v.includes('allow') || v.includes('permit') || v.includes('pass')) return 'success';
  return 'informative';
};

const isPassingColor = (isPassing: boolean): BadgeProps['color'] => (isPassing ? 'success' : 'danger');

// ---------------------------------------------------------------------------
// Manual form validation — one function per rule, matching `mcpServerFormFields.tsx` conventions.
// ---------------------------------------------------------------------------

type FieldErrorMap = Record<string, string>;

function identityExternalIdRequiredError(value: string): string | undefined {
  return value.trim() ? undefined : 'Identity external ID is required';
}

function requestedActionRequiredError(value: string): string | undefined {
  return value.trim() ? undefined : 'Requested action is required';
}

function executionModeRequiredError(value: string): string | undefined {
  return value ? undefined : 'Select an execution mode';
}

// ---------------------------------------------------------------------------
// Run history — server-side skip/take pagination, wrapped into the shared `PagedResult` shape.
// ---------------------------------------------------------------------------

const RUN_HISTORY_PAGE_SIZE = 25;
const RUN_HISTORY_THRESHOLD = 200;

async function fetchRunsPage(params: ListQueryParams): Promise<PagedResult<SimulationResult>> {
  const take = params.pageSize;
  const skip = (params.page - 1) * take;
  const result: SimulationRunPagedResult = await executionSimulatorApi.getRuns(skip, take);
  const totalPages = Math.max(Math.ceil(result.totalCount / take), 1);
  return {
    items: result.items,
    totalCount: result.totalCount,
    pageNumber: params.page,
    pageSize: take,
    totalPages,
    hasPreviousPage: params.page > 1,
    hasNextPage: params.page < totalPages,
  };
}

function getSimulationRunSortValue(item: SimulationResult, field: string): unknown {
  return (item as unknown as Record<string, unknown>)[field];
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: `0 ${tokens.spacingHorizontalL} ${tokens.spacingVerticalL}`,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },
  resultBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    padding: `0 ${tokens.spacingHorizontalL} ${tokens.spacingVerticalL}`,
  },
  resultHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  listSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
});

// ---------------------------------------------------------------------------
// SimulationResultCard — shared between the inline post-submit render and the run-history
// detail drawer, per the page brief ("factor it into one shared sub-component").
// ---------------------------------------------------------------------------

const SimulationResultCard: FC<{ result: SimulationResult }> = ({ result }) => {
  const styles = useStyles();
  return (
    <div className={styles.resultBody} data-testid="simulation-result-card">
      <div className={styles.resultHeaderRow}>
        <Badge size="large" appearance="tint" color={verdictColor(result.predictedVerdict)}>
          {result.predictedVerdict}
        </Badge>
        <Badge size="large" appearance="tint" color={isPassingColor(result.isPassing)}>
          {result.isPassing ? 'Passing' : 'Failing'}
        </Badge>
      </div>
      <DetailField label="Reason">
        <Body1>{result.reason || '—'}</Body1>
      </DetailField>
      <DetailField label="Matched Rules">
        {result.matchedRules.length > 0 ? (
          <DetailBadges>
            {result.matchedRules.map((rule) => (
              <Badge key={rule} appearance="outline">
                {rule}
              </Badge>
            ))}
          </DetailBadges>
        ) : (
          <Caption1>None</Caption1>
        )}
      </DetailField>
      <DetailField label="Constraints">
        {result.constraints.length > 0 ? (
          <DetailBadges>
            {result.constraints.map((constraint) => (
              <Badge key={constraint} appearance="outline" color="warning">
                {constraint}
              </Badge>
            ))}
          </DetailBadges>
        ) : (
          <Caption1>None</Caption1>
        )}
      </DetailField>
      <DetailMetadataGrid>
        <DetailField label="Blast Radius Score">
          <Text>{result.blastRadiusScore}</Text>
        </DetailField>
        <DetailField label="Propagation Potential">
          <Text>{result.propagationPotential}</Text>
        </DetailField>
        <DetailField label="Simulation ID">
          <Text style={{ fontFamily: 'monospace' }}>{result.simulationId}</Text>
        </DetailField>
        <DetailField label="Created">
          <Text>{new Date(result.createdAt).toLocaleString()}</Text>
        </DetailField>
      </DetailMetadataGrid>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SimulationRunDetailDrawer
// ---------------------------------------------------------------------------

const SimulationRunDetailDrawer: FC<{ simulationId: string | null; onClose: () => void }> = ({
  simulationId,
  onClose,
}) => {
  const { data, isLoading, isError } = useQuery<SimulationResult>({
    queryKey: ['simulation-run-detail', simulationId],
    queryFn: () => executionSimulatorApi.getRun(simulationId!),
    enabled: simulationId !== null,
  });

  return (
    <DetailDrawer open={simulationId !== null} onClose={onClose} title="Simulation Run Detail">
      {isLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
          <Spinner label="Loading simulation…" />
        </div>
      )}
      {isError && <ErrorState message="Unable to load this simulation run." />}
      {data && <SimulationResultCard result={data} />}
    </DetailDrawer>
  );
};

// ---------------------------------------------------------------------------
// ExecutionSimulatorPage
// ---------------------------------------------------------------------------

export const ExecutionSimulatorPage: FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const queryClient = useQueryClient();
  const toasterId = useId('execution-simulator-toaster');
  const { dispatchToast } = useToastController(toasterId);

  // --- Form state ---
  const [identityExternalId, setIdentityExternalId] = useState('');
  const [targetExternalId, setTargetExternalId] = useState('');
  const [requestedAction, setRequestedAction] = useState('');
  const [executionMode, setExecutionMode] = useState('');
  const [reversibility, setReversibility] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap>({});
  const [globalError, setGlobalError] = useState<string | undefined>();
  const [lastResult, setLastResult] = useState<SimulationResult | null>(null);

  const setFieldError = (field: string, message: string | undefined) => {
    setFieldErrors((prev) => {
      if (!message) {
        if (!(field in prev)) return prev;
        const next = { ...prev };
        delete next[field];
        return next;
      }
      return { ...prev, [field]: message };
    });
  };

  const simulateMutation = useMutation({
    mutationFn: (input: SimulateExecutionInput) => executionSimulatorApi.simulate(input),
    onSuccess: (result) => {
      setLastResult(result);
      setGlobalError(undefined);
      queryClient.invalidateQueries({ queryKey: ['simulation-runs'] });
      dispatchToast(
        <Toast>
          <ToastTitle>Simulation Complete</ToastTitle>
          <ToastBody>Predicted verdict: {result.predictedVerdict}</ToastBody>
        </Toast>,
        { intent: result.isPassing ? 'success' : 'warning', timeout: 6000 }
      );
    },
    onError: (error) => {
      setGlobalError(error instanceof Error ? error.message : 'Unable to run the simulation.');
      dispatchToast(
        <Toast>
          <ToastTitle>Simulation Failed</ToastTitle>
          <ToastBody>Unable to run the simulation. Please try again.</ToastBody>
        </Toast>,
        { intent: 'error', timeout: 6000 }
      );
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    // Guard double-submit.
    if (simulateMutation.isPending) return;
    setGlobalError(undefined);

    const errors: FieldErrorMap = {};
    const identityErr = identityExternalIdRequiredError(identityExternalId);
    if (identityErr) errors.identityExternalId = identityErr;
    const actionErr = requestedActionRequiredError(requestedAction);
    if (actionErr) errors.requestedAction = actionErr;
    const modeErr = executionModeRequiredError(executionMode);
    if (modeErr) errors.executionMode = modeErr;

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const input: SimulateExecutionInput = {
      identityExternalId: identityExternalId.trim(),
      targetExternalId: targetExternalId.trim() || undefined,
      requestedAction: requestedAction.trim(),
      executionMode,
      reversibility: reversibility || undefined,
    };

    simulateMutation.mutate(input);
  };

  // --- Run history grid ---
  const [page, setPage] = useState(1);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  const grid = useAdaptiveGridData<SimulationResult>({
    queryKeyBase: ['simulation-runs'],
    fetchPage: fetchRunsPage,
    page,
    pageSize: RUN_HISTORY_PAGE_SIZE,
    sortState: [{ field: 'createdAt', dir: 'desc' }],
    getValue: getSimulationRunSortValue,
    threshold: RUN_HISTORY_THRESHOLD,
  });

  const {
    items: runs,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = grid;

  // Out-of-range clamp (§9) — never strand the user on an empty page after a new run pushes
  // the total count down a page boundary.
  useEffect(() => {
    if (!isLoading && page > totalPages) {
      setPage(totalPages);
    }
  }, [isLoading, page, totalPages]);

  const columnCount = 5; // createdAt, verdict, passing, blastRadiusScore, propagationPotential
  const actionButtonCount = 1; // chevron detail

  let listBody: ReactNode;
  if (isLoading) {
    listBody = <LoadingSkeleton rows={5} columns={columnCount + 1} />;
  } else if (isError) {
    listBody = <ErrorState message="Unable to load the simulation run history." onRetry={refetch} />;
  } else if (runs.length === 0) {
    listBody = (
      <EmptyState
        icon={<HistoryRegular />}
        title="No Simulation Runs Yet"
        description="Run a hypothetical execution above to see its predicted verdict recorded here."
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={isFetching && !isLoading}>
        <Table aria-label="Simulation run history">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Created At</TableHeaderCell>
              <TableHeaderCell>Predicted Verdict</TableHeaderCell>
              <TableHeaderCell>Passing</TableHeaderCell>
              <TableHeaderCell>Blast Radius Score</TableHeaderCell>
              <TableHeaderCell>Propagation Potential</TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(actionButtonCount) }}>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.map((item) => (
              <TableRow key={item.simulationId}>
                <TableCell>
                  <Text>{new Date(item.createdAt).toLocaleString()}</Text>
                </TableCell>
                <TableCell>
                  <Badge appearance="tint" color={verdictColor(item.predictedVerdict)}>
                    {item.predictedVerdict}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge appearance="tint" color={isPassingColor(item.isPassing)}>
                    {item.isPassing ? 'Passing' : 'Failing'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Text>{item.blastRadiusScore}</Text>
                </TableCell>
                <TableCell>
                  <Text>{item.propagationPotential}</Text>
                </TableCell>
                <TableCell>
                  <Button
                    appearance="subtle"
                    icon={<ChevronRightRegular />}
                    size="small"
                    aria-label="View simulation run details"
                    onClick={() => setSelectedRunId(item.simulationId)}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={ps.page}>
      <Toaster toasterId={toasterId} position="top-end" />

      <PageHeader
        title="Execution Simulator"
        subtitle="Run a hypothetical execution request through REGE's read-only decision path — no real execution"
        icon={<BeakerRegular />}
      />

      <Card>
        <CardHeader
          header={<Text weight="semibold">Simulate a Hypothetical Execution</Text>}
          description={<Caption1>No F037 ledger write, no real execution decision — read-only preview.</Caption1>}
        />
        <form onSubmit={handleSubmit} className={styles.form}>
          {globalError && (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>Unable to Run Simulation</MessageBarTitle>
                {globalError}
              </MessageBarBody>
            </MessageBar>
          )}

          <Field
            label="Identity External ID"
            required
            validationState={fieldErrors.identityExternalId ? 'error' : undefined}
            validationMessage={fieldErrors.identityExternalId}
          >
            <Input
              value={identityExternalId}
              onChange={(_, data) => {
                setIdentityExternalId(data.value);
                setFieldError('identityExternalId', undefined);
              }}
              placeholder="agent:..."
              disabled={simulateMutation.isPending}
            />
          </Field>

          <Field label="Target External ID" hint="Optional — external ID of the request's target">
            <Input
              value={targetExternalId}
              onChange={(_, data) => setTargetExternalId(data.value)}
              placeholder="resource:..."
              disabled={simulateMutation.isPending}
            />
          </Field>

          <Field
            label="Requested Action"
            required
            validationState={fieldErrors.requestedAction ? 'error' : undefined}
            validationMessage={fieldErrors.requestedAction}
          >
            <Input
              value={requestedAction}
              onChange={(_, data) => {
                setRequestedAction(data.value);
                setFieldError('requestedAction', undefined);
              }}
              placeholder="datasource.read"
              disabled={simulateMutation.isPending}
            />
          </Field>

          <Field
            label="Execution Mode"
            required
            validationState={fieldErrors.executionMode ? 'error' : undefined}
            validationMessage={fieldErrors.executionMode}
          >
            <Dropdown
              placeholder="Select execution mode"
              value={executionMode}
              selectedOptions={executionMode ? [executionMode] : []}
              disabled={simulateMutation.isPending}
              onOptionSelect={(_, data) => {
                const next = (data.optionValue as string) || '';
                setExecutionMode(next);
                setFieldError('executionMode', executionModeRequiredError(next));
              }}
            >
              {EXECUTION_MODE_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <Field label="Reversibility" hint="Optional — how reversible is this action?">
            <Dropdown
              placeholder="Select reversibility (optional)"
              value={reversibility}
              selectedOptions={reversibility ? [reversibility] : []}
              disabled={simulateMutation.isPending}
              onOptionSelect={(_, data) => setReversibility((data.optionValue as string) || '')}
            >
              <Option value="">Not Specified</Option>
              {REVERSIBILITY_OPTIONS.map((v) => (
                <Option key={v} value={v}>
                  {v}
                </Option>
              ))}
            </Dropdown>
          </Field>

          <div className={styles.actions}>
            <Button
              type="submit"
              appearance="primary"
              icon={simulateMutation.isPending ? <Spinner size="tiny" /> : <PlayRegular />}
              disabled={simulateMutation.isPending}
            >
              {simulateMutation.isPending ? 'Running…' : 'Run Simulation'}
            </Button>
          </div>
        </form>
      </Card>

      {lastResult && (
        <Card>
          <CardHeader
            header={<Text weight="semibold">Simulation Result</Text>}
            description={<Caption1>{new Date(lastResult.createdAt).toLocaleString()}</Caption1>}
          />
          <SimulationResultCard result={lastResult} />
        </Card>
      )}

      <div className={styles.listSection}>
        <ListPageLayout
          header={
            <MasterDetailHeader
              crumbs={[{ label: 'Run History' }]}
              busy={isFetching && !isLoading}
              subtitle="Recent hypothetical execution simulations, most recent first"
            />
          }
          footer={
            <Pager
              page={page}
              totalPages={totalPages}
              hasPreviousPage={hasPreviousPage}
              hasNextPage={hasNextPage}
              totalCount={totalCount}
              onPageChange={setPage}
              testId="execution-simulator-pager"
            />
          }
        >
          {listBody}
        </ListPageLayout>
      </div>

      <SimulationRunDetailDrawer simulationId={selectedRunId} onClose={() => setSelectedRunId(null)} />
    </div>
  );
};
